import axios, { CanceledError } from "axios";
import runtime from "config/runtime";

const API_URL = runtime.apiUrl;

/**
 * Reproduce axios 0.x query-string encoding.
 *
 * axios 1.x percent-encodes square brackets (`filter=%5Bstatus%5D%3DActive`) where 0.x
 * left them literal (`filter=[status]%3DActive`). Both forms decode to the same value
 * through any compliant query parser, but the kernel's filter grammar is bracket-based
 * and this upgrade was done to clear CVEs -- not to change what goes on the wire. Pinning
 * the serializer keeps the request bytes identical to what the backend has always
 * received, so the upgrade cannot regress filtering.
 *
 * Mirrors axios 0.27.2 `helpers/buildURL.js`.
 */
const encode0x = (val) =>
  encodeURIComponent(val)
    .replace(/%3A/gi, ":")
    .replace(/%24/g, "$")
    .replace(/%2C/gi, ",")
    .replace(/%20/g, "+")
    .replace(/%5B/gi, "[")
    .replace(/%5D/gi, "]");

const serializeParams = (params) => {
  const parts = [];

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || typeof value === "undefined") {
      return;
    }

    // 0.x appended `key[]` once per element for array values.
    const values = Array.isArray(value) ? value : [value];
    const paramKey = Array.isArray(value) ? `${key}[]` : key;

    values.forEach((entry) => {
      let serialized = entry;

      if (entry instanceof Date) {
        serialized = entry.toISOString();
      } else if (entry !== null && typeof entry === "object") {
        serialized = JSON.stringify(entry);
      }

      parts.push(`${encode0x(paramKey)}=${encode0x(serialized)}`);
    });
  });

  return parts.join("&");
};

const instance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  paramsSerializer: { serialize: serializeParams },
});

// Request interceptor to handle "Not Set" values in URL parameters
instance.interceptors.request.use(
  (config) => {
    // Check if the URL contains "Not Set" or "Not%20Set"
    if (config.url && (config.url.includes('/Not Set') || config.url.includes('/Not%20Set'))) {
      // Cancel the request by returning a rejected promise with a specific error
      const cancelError = new CanceledError('Request cancelled: URL contains "Not Set" parameter');
      cancelError.isNotSetCancel = true; // Custom flag to identify this type of cancellation
      return Promise.reject(cancelError);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to silently handle "Not Set" cancellations
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    // If this is a "Not Set" cancellation, return empty data instead of error
    if (axios.isCancel(error) && error.isNotSetCancel) {
      return Promise.resolve({ data: {} });
    }
    return Promise.reject(error);
  }
);

export default instance;
export { serializeParams };
